import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { 
  AlertTriangle,
  Droplets,
  FileText,
  Wrench,
  Timer,
  Calendar,
  ChevronDown
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroImage from "@/assets/retaining-wall-repair-bergen-county-nj.webp";
import retainingWallDiagram from "@/assets/retaining-wall-diagram-masonry-pro-construction-totowa-nj.webp";
import permitImage from "@/assets/retaining-wall-construction-little-falls-passaic-county-nj.webp";
import parsippanyWallImage from "@/assets/retaining-wall-replacement-parsippany-troy-hills-morris-county-nj.webp";

const faqs = [
  {
    question: "How do I know if my retaining wall in North Jersey needs repair or full replacement?",
    answer: "Minor cracks, loose mortar, and small drainage issues usually indicate repair is sufficient. A wall that leans more than two inches, has foundation settlement, or shows widespread instability likely requires a rebuild. A masonry contractor can assess this during a free on-site inspection.",
  },
  {
    question: "Do I need a permit to repair a retaining wall in North Jersey?",
    answer: "Walls under four feet typically require a zoning permit. Walls over four feet require a construction permit and engineering calculations from a NJ-licensed Professional Engineer. Your contractor can handle the permit paperwork.",
  },
  {
    question: "Who is responsible for a retaining wall between two properties in New Jersey?",
    answer: "The property owner whose land the wall supports is typically responsible. If a shared boundary is involved, both owners may share responsibility. Check your property survey and consult your municipality's code enforcement office.",
  },
  {
    question: "Does homeowners insurance cover retaining wall repair in New Jersey?",
    answer: "Most policies cover sudden damage from covered events such as a fallen tree or vehicle impact. Gradual deterioration, poor drainage, and normal wear are usually excluded. Document your wall's condition with photos before filing any claim.",
  },
  {
    question: "Can I repair a retaining wall myself in North Jersey?",
    answer: "Minor cosmetic fixes like repointing small mortar joints and clearing weep holes are manageable. Structural repairs on walls over four feet require a permit and engineering sign-off. Improper DIY repair on a load-bearing wall risks collapse and code violations.",
  },
  {
    question: "How long does a professional retaining wall repair take in North Jersey?",
    answer: "Most targeted repairs including drainage fixes, anchor installation, and repointing take one to three days. A full section rebuild may take one to two weeks depending on wall height, permit timeline, and weather conditions.",
  },
];

const citations = [
  {
    id: 1,
    text: "CMHA (Concrete Masonry & Hardscapes Association), Segmental Retaining Walls Best Practices Guide – Industry authority on retaining wall design, drainage, and construction best practices.",
    url: "https://www.masonryandhardscapes.org/resource/srw-man-002/",
  },
  {
    id: 2,
    text: "ASCE (American Society of Civil Engineers), Avoiding Failures of Retaining Walls – Professional engineering source on failure causes, distress signs, and repair strategies.",
    url: "https://www.asce.org/education-and-events/explore-education/on-demand-webinars/avoiding-failures-of-retaining-walls",
  },
  {
    id: 3,
    text: "Structure Magazine (ASCE-affiliated), Periodic Inspection of Retaining Walls – Covers retaining wall inspection standards and condition categories.",
    url: "https://www.structuremag.org/article/periodic-inspection-of-retaining-walls/",
  },
  {
    id: 4,
    text: "FHWA (Federal Highway Administration), GEC No. 11: Design and Construction of MSE Walls (FHWA-NHI-10-024) – Federal guidelines for retaining wall failure modes, drainage, and reinforcement.",
    url: "https://www.fhwa.dot.gov/engineering/geotech/pubs/nhi10024/",
  },
  {
    id: 5,
    text: "U.S. Army Corps of Engineers (USACE), EM 1110-2-2502: Retaining and Flood Walls – Federal engineering manual on retaining wall stability, design loads, and drainage requirements.",
    url: "https://www.publications.usace.army.mil/portals/76/publications/engineermanuals/em_1110-2-2502.pdf",
  },
  {
    id: 6,
    text: "NJ Dept. of Environmental Protection, N.J.A.C. 7:13-12.13: Requirements for Retaining Walls – NJ state regulation requiring stable footings and engineering certification for walls 4 ft+.",
    url: "https://www.law.cornell.edu/regulations/new-jersey/N-J-A-C-7-13-12-13",
  },
  {
    id: 7,
    text: "ICC Digital Codes (NJ Edition), 2018 IRC NJ Edition, Section R404.4: Retaining Walls – NJ building code requiring walls over 4 ft to be designed by a licensed PE.",
    url: "https://codes.iccsafe.org/s/NJRC2018P2/chapter-4-foundations/NJRC2018P2-Pt03-Ch04-SecR404.4",
  },
];

const youtubeVideoIds = ["-G1xdMCcks0", "FR50G7k4qDU", "ktQRtcBf70o"];

export default function RetainingWallRepair() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Retaining Wall Repair Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="Wall leaning or cracking? We fix retaining walls across Passaic, Bergen, Essex, and Morris Counties. Honest advice on repair versus rebuild, no pressure." />
        <meta property="og:title" content="Retaining Wall Repair Totowa, NJ | Masonry Pro Construction" />
        <meta property="og:description" content="Wall leaning or cracking? We fix retaining walls across Passaic, Bergen, Essex, and Morris Counties. Honest advice on repair versus rebuild, no pressure." />
        <meta name="twitter:title" content="Retaining Wall Repair NJ - Leaning, Cracking | Masonry Pro" />
        <meta name="twitter:description" content="Retaining wall leaning, bulging, or pulling apart? Free on-site assessment across Bergen, Essex, Passaic, Morris. Repair vs rebuild advice you can trust." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Retaining Wall Repair",
            description: "Retaining wall repair for block, stone, and concrete walls across North Jersey.",
            url: "/masonry-services/retaining-wall-repair",
            image: "https://masonry-pro.com/og/retaining-wall-repair.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/retaining-wall-repair.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/retaining-wall-repair.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Retaining wall repair in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Retaining Wall Repair in Totowa, NJ Serving North Jersey: Fix Cracks, Leaning, and Drainage Problems <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Before They Get Worse</span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', marginBottom: '1.25rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Is your retaining wall leaning, cracking, or pulling apart? You're not alone, and you're probably not looking at a full rebuild either.
                </p>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', marginBottom: '1.25rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  We've been doing retaining wall repair across North Jersey for over 35 years. Most of the damaged walls we see in Bergen, Passaic, Essex, and Morris Counties can be fixed without tearing the whole thing down, especially when drainage is the underlying cause. Clay-heavy soil and 40+ freeze-thaw cycles each winter put constant pressure on these walls, so failure is usually about how the wall was built and drained, not about how old it is.
                </p>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', marginBottom: '2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Send us a few photos or call for a free on-site look. We'll tell you what's wrong and what the right repair actually looks like.
                </p>
                <Button variant="gold" size="lg" asChild>
                  <a href="/contact">Get a Free Inspection</a>
                </Button>
              </div>
            </div>
          </section>

          {/* Cracking, Leaning, Bulging Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="h-6 w-6 text-secondary" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold">
                      Cracking, Leaning, and Bulging Are the First Signs <span className="text-gradient-gold">a Retaining Wall Needs Repair</span>
                    </h2>
                  </div>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Horizontal cracks can be more serious than vertical ones. A vertical crack usually means minor settling. A horizontal crack could mean the soil behind the wall is pushing harder than the wall can handle.<sup>²</sup> Wall repair is a big part of what we do as a <Link to="/" className="text-secondary hover:underline">Totowa masonry contractor</Link>.
                    </p>
                    <p>
                      A retaining wall that is leaning forward or bulging at the center can tell you the same thing. The pressure is winning. North Jersey's clay-heavy glacial soil expands when wet and shrinks when dry. That constant push-pull cycle accelerates damage in block and stone walls.
                    </p>
                    <p>
                      Homeowners in Montclair and Wayne see this often. Hillside properties in those areas are on soil that never remains stable. If your wall shows any of these signs, it's worth getting a professional look before the damage spreads.
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden border border-border">
                  <img
                    src={parsippanyWallImage}
                    alt="Retaining wall replacement transforms a deteriorating wall into a tiered stone wall with steps in Parsippany-Troy Hills, Morris County, NJ."
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Poor Drainage Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                <div className="rounded-xl overflow-hidden border border-border">
                  <img
                    src={retainingWallDiagram}
                    alt="Retaining wall installation diagram showing incorrect wall failure due to trapped water pressure versus correct wall with proper construction and drainage — 36 Highview Ave, Totowa, NJ 07512"
                    className="w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center flex-shrink-0">
                      <Droplets className="h-6 w-6 text-secondary" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold">
                      Poor Drainage Is the Leading Cause of <span className="text-gradient-gold">Retaining Wall Failure</span> in North Jersey
                    </h2>
                  </div>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Water trapped behind a retaining wall creates hydrostatic pressure. That pressure can push the wall forward, crack mortar joints, and eventually cause failure.<sup>¹</sup> Most of the damaged walls we inspect in Ridgewood and Glen Rock have drainage problems.
                    </p>
                    <p>
                      North Jersey averages 50 inches of rain per year. Combine that with 40+ freeze-thaw cycles and water expands 9% when it freezes. That expansion can crack block, stone, and concrete joints from the inside out.
                    </p>
                    <p>
                      Weep holes, French drains, and gravel backfill are the three most common drainage repairs. Seasonal drainage checks are worth doing, especially after winter. Give us a call, and we can assess whether your wall's drainage system is still functioning properly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Permits Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                <div className="rounded-xl overflow-hidden border border-border">
                  <img
                    src={permitImage}
                    alt="Construction of a new retaining wall for a residential property in Little Falls, Passaic County, NJ."
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center flex-shrink-0">
                      <FileText className="h-6 w-6 text-secondary" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold">
                      North Jersey Retaining Walls Over Four Feet <span className="text-gradient-gold">Require a Permit and Engineer</span>
                    </h2>
                  </div>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Walls under four feet typically require a zoning permit only. Walls over four feet require a construction permit and engineering calculations from a NJ-licensed Professional Engineer.<sup>⁷</sup> Footings must extend at least three feet below grade per NJ Administrative Code.<sup>⁶</sup>
                    </p>
                    <p>
                      Permit requirements can vary by municipality. Millburn, Nutley, and Garfield each have specific retaining wall application processes. An experienced masonry contractor in North Jersey can handle the permit paperwork as part of the job.
                    </p>
                    <p>
                      Our team coordinates with engineers regularly and manages the submission process for homeowners across <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link>, <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic</Link>, and <Link to="/service-areas/morris-county" className="text-secondary hover:underline">Morris</Link> Counties. If you're unsure what your project requires, give us a call and we can walk you through it.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Repair Methods + Seasonal Inspections — 2-column */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">

                {/* Left: Repair Methods */}
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center flex-shrink-0">
                      <Wrench className="h-6 w-6 text-secondary" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold">
                      Most Damaged Retaining Walls Can Be Repaired <span className="text-gradient-gold">Without a Full Rebuild</span>
                    </h2>
                  </div>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Not every damaged wall needs to come down. Common repair methods include wall anchors and tiebacks for leaning walls, new drainage systems for water problems, shotcrete facings for surface damage, selective block or stone replacement, and repointing mortar joints.<sup>⁴</sup> A full rebuild is typically only necessary when the foundation has failed.
                    </p>
                    <p>
                      Many homes in Paterson, Clifton, and Woodland Park sit on sloped lots with retaining walls from the 1960s through 1980s. These older block walls often need drainage upgrades and joint repair rather than total replacement.
                    </p>
                    <p>
                      Give us a call, and we can determine whether your wall can be repaired or if a <Link to="/masonry-services/retaining-walls" className="text-secondary hover:underline">full retaining wall installation</Link> is the better option.
                    </p>
                  </div>
                </div>

                {/* Right: Seasonal Inspections */}
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center flex-shrink-0">
                      <Calendar className="h-6 w-6 text-secondary" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold">
                      Seasonal Inspections Help Prevent <span className="text-gradient-gold">Costly Retaining Wall Failures</span>
                    </h2>
                  </div>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Retaining walls should be inspected annually. New cracks, forward lean, blocked weep holes, soil erosion at the base, and vegetation growing through joints are all indicators that something may be wrong.<sup>³</sup> Identifying these issues early can help avoid expensive emergency repairs.
                    </p>
                    <p>
                      Spring inspections after North New Jersey's winter are especially important. Frost heave and snowmelt can shift walls overnight. Post-storm assessments also matter in flood-prone areas like Little Falls and Pompton Plains.
                    </p>
                    <p>
                      HOA boards and rental property owners in particular benefit from scheduled inspections to reduce liability. Give us a call, and we can discuss a seasonal inspection plan for your walls.
                    </p>
                  </div>
                </div>

              </div>
            </div>
          </section>

          {/* Featured Snippet Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto bg-background rounded-xl p-8 border border-border">
                <h2 className="text-2xl md:text-3xl font-bold mb-6">
                  What causes retaining wall failure in North Jersey?
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Retaining wall failure in North Jersey occurs when the wall can no longer hold back the soil behind it. The most common cause is water buildup from poor drainage, made worse by the region's clay soil and 40+ freeze-thaw cycles each winter.
                </p>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-secondary mt-2 flex-shrink-0" />
                    <span>Hydrostatic pressure from clogged or missing drain systems</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-secondary mt-2 flex-shrink-0" />
                    <span>Freeze-thaw expansion cracking block, stone, and concrete joints</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-secondary mt-2 flex-shrink-0" />
                    <span>Foundation settling in North Jersey's variable glacial soil conditions</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* YouTube Shorts Section */}
          <YouTubeShortsSection 
            title="See Our Retaining Wall Repair Work"
            highlightedTitle="in North Jersey"
            videoIds={youtubeVideoIds}
          />

          {/* FAQ Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked Questions
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq) => (
                  <div key={faq.question} className="bg-card rounded-xl p-6 border border-border">
                    <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="masonry" />

          {/* CTA Section */}
          <CTASection 
            heading="Ready to Fix Your"
            highlightedHeading="Retaining Wall?"
            serviceName="retaining wall repair"
          />

          {/* Citations Section */}
          <section className="py-12 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible>
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                      <span className="flex items-center gap-2">
                        Citations & References
                        <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ol className="space-y-3 text-sm text-muted-foreground list-decimal list-inside">
                        {citations.map((citation) => (
                          <li key={citation.id}>
                            {citation.text}{" "}
                            <a 
                              href={citation.url} 
                              className="text-secondary hover:underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {citation.url}
                            </a>
                          </li>
                        ))}
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
